import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Save, CheckIcon, HourglassIcon, Trash2 } from "lucide-react";
import { InterViewSlotService } from "@/services/InterViewSlot";
import Loader from "@/components/Loader";
import PageHeader from "@/components/Common/PageHeader";
import useToastStore from "@/components/Common/Toast/toastStore";
import DialogBoxSkeleton from "@/components/Common/DialogBoxSkeleton";
import DialogBox from "@/components/Common/DialogBox";
import InterviewDetailsForm from "@/components/candidates/InterviewDetailsForm";

const slotDurationOptions = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60];
const breakDurationOptions = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60];

const slotIsDeletable = (item) =>
  item.type === "slot" && item.status === "open" && item.slot?.id != null;

const InterviewSlotDetails = () => {
  const navigate = useNavigate();
  const { vacancyId, statusId } = useParams();
  const location = useLocation();
  const { showToast } = useToastStore;

  const [blocks, setBlocks] = useState([]);
  const [editState, setEditState] = useState({}); // { [blockId]: { fromTime, toTime, slotDuration, breakDuration } }
  const [savingId, setSavingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [showSlotsPopup, setShowSlotsPopup] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  /** { [blockId]: number[] } slot ids selected for bulk delete */
  const [selectedSlotIdsByBlock, setSelectedSlotIdsByBlock] = useState({});
  const [blockToDelete, setBlockToDelete] = useState(null);
  const [slotsDeletePayload, setSlotsDeletePayload] = useState(null); // { slotIds: number[] }
  const [deletingBlockId, setDeletingBlockId] = useState(null);
  const [deletingSlots, setDeletingSlots] = useState(false);

  const handleBack = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(-1);
  };

  useEffect(() => {
    if (vacancyId && statusId) {
      fetchDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vacancyId, statusId]);

  // Handle auto-opening a slot if passed in from navigation state
  useEffect(() => {
    if (blocks.length > 0 && location.state?.autoOpenSlotId) {
      const slotId = location.state.autoOpenSlotId;
      let foundBlock = null;
      let foundSlot = null;

      for (const block of blocks) {
        if (block.slots) {
          const slot = block.slots.find((s) => s.id === slotId);
          if (slot) {
            foundBlock = block;
            foundSlot = slot;
            break;
          }
        }
      }

      if (foundBlock && foundSlot) {
        // Prepare state and open popup
        setSelectedJob({
          company_availability_blocks: blocks,
          booked_interview_slot: {
            ...foundBlock,
            slots: [foundSlot],
          },
        });
        setShowSlotsPopup(true);

        // Clear state to avoid reopening on every re-render
        window.history.replaceState({}, document.title);
      }
    }
  }, [blocks, location.state]);

  const fetchDetails = async () => {
    setIsLoading(true);
    try {
      const data = await InterViewSlotService.getVacancyStatusBlocks(
        vacancyId,
        statusId,
      );
      const list = data.data || [];
      setBlocks(list);
      setMeta(data);
      // Initialise edit state from fetched data
      const initial = {};
      list.forEach((block) => {
        const startTimePart = block.start_time?.includes(" ")
          ? block.start_time.split(" ")[1]
          : block.start_time;
        const endTimePart = block.end_time?.includes(" ")
          ? block.end_time.split(" ")[1]
          : block.end_time;
        // Store as HH:MM for <input type="time">
        initial[block.id] = {
          fromTime: startTimePart?.slice(0, 5) || "",
          toTime: endTimePart?.slice(0, 5) || "",
          slotDuration: String(block.slot_duration || ""),
          breakDuration: String(block.break_duration || "0"),
        };
      });
      setEditState(initial);
      setSelectedSlotIdsByBlock({});
    } catch (error) {
      console.error("Failed to fetch slot details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldChange = (blockId, field, value) => {
    setEditState((prev) => ({
      ...prev,
      [blockId]: { ...prev[blockId], [field]: value },
    }));
  };

  const handleSave = async (block) => {
    const edit = editState[block.id];
    if (!edit) return;

    const dateStr = block.date || block.start_time?.split(" ")[0];

    // Flat payload as required by the backend validation rules
    const companyId = meta?.vacancy?.company_id || block.company_id;

    if (!companyId) {
      showToast({
        message: "Could not determine company. Please refresh and try again.",
        type: "error",
      });
      return;
    }

    setSavingId(block.id);
    try {
      await InterViewSlotService.updateBlock(block.id, {
        company_id: companyId,
        vacancy_id: vacancyId,
        vacancy_status_id: statusId,
        date: dateStr,
        start_time: `${dateStr} ${edit.fromTime}:00`,
        end_time: `${dateStr} ${edit.toTime}:00`,
        slot_duration: parseInt(edit.slotDuration),
        break_duration: parseInt(edit.breakDuration) || 0,
      });
      showToast({ message: "Block updated successfully!", type: "success" });
      fetchDetails();
    } catch (error) {
      showToast({
        message: error.message || "Failed to update block.",
        type: "error",
      });
    } finally {
      setSavingId(null);
    }
  };

  // Format time: "14:30" → "2:30 PM"
  const formatTime = (timeStr) => {
    if (!timeStr) return "-";
    const [h, m] = timeStr.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour = h % 12 || 12;
    return `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
  };

  const parseDateString = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const formatDayName = (dateStr) => {
    const d = parseDateString(dateStr);
    if (!d) return "";
    return d.toLocaleDateString("en-US", { weekday: "long" });
  };

  const formatFullDate = (dateStr) => {
    const d = parseDateString(dateStr);
    if (!d) return "";
    return d.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  // Build slot/break timeline from edit state values
  const buildTimeline = (block) => {
    const edit = editState[block.id];
    if (!edit?.fromTime || !edit?.toTime || !edit?.slotDuration) return [];

    const toMinutes = (t) => {
      const [h, m] = t.split(":").map(Number);
      return h * 60 + m;
    };
    const fromMinutes = (mins) => {
      const h = Math.floor(mins / 60) % 24;
      const m = mins % 60;
      const ampm = h >= 12 ? "PM" : "AM";
      const hour = h % 12 || 12;
      return `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
    };

    const items = [];
    let current = toMinutes(edit.fromTime);
    const end = toMinutes(edit.toTime);
    const slotDur = parseInt(edit.slotDuration);
    const breakDur = parseInt(edit.breakDuration) || 0;

    if (isNaN(slotDur) || slotDur <= 0 || end <= current) return [];

    const old_slots = block?.slots;
    let items_index = 0;
    while (current + slotDur <= end) {
      const slotEnd = current + slotDur;
      const currentSlot = old_slots?.[items_index];
      items.push({
        type: "slot",
        status: currentSlot?.status ?? "open",
        slot: currentSlot,
        from: fromMinutes(current),
        to: fromMinutes(slotEnd),
        duration: slotDur,
        startMins: current,
        endMins: slotEnd,
      });
      current = slotEnd;
      if (breakDur > 0 && current < end) {
        items.push({ type: "break", duration: breakDur });
        current += breakDur;
      }
      items_index++;
    }

    const dateTimeToMinutes = (dateTimeStr) => {
      if (!dateTimeStr) return NaN;
      const timePart = dateTimeStr.includes(" ")
        ? dateTimeStr.split(" ")[1]
        : dateTimeStr;
      const [h, m] = timePart.split(":").map(Number);
      return h * 60 + m;
    };

    const apiSlots = block.slots || [];

    const segmentWithinSlot = (item, s) => {
      if (!s?.start_time || !s?.end_time) return false;
      const sm = dateTimeToMinutes(s.start_time);
      const em = dateTimeToMinutes(s.end_time);
      if (Number.isNaN(sm) || Number.isNaN(em)) return false;
      return sm <= item.startMins && item.endMins <= em;
    };

    const mapped = items.map((item) => {
      if (item.type !== "slot" || apiSlots.length === 0) return item;
      const match = apiSlots.find((s) => segmentWithinSlot(item, s));
      if (!match) return item;
      return { ...item, slot: match, status: match.status ?? item.status };
    });

    let lo = 0;
    let hi = mapped.length;
    while (lo < hi && mapped[lo].type === "break") lo++;
    while (hi > lo && mapped[hi - 1].type === "break") hi--;
    return mapped.slice(lo, hi);
  };

  const handleViewSlots = (block, slot) => {
    if (!slot) return;
    setSelectedJob({
      company_availability_blocks: blocks,
      booked_interview_slot: {
        ...block,
        slots: [slot],
      },
    });
    setShowSlotsPopup(true);
  };

  const handleCloseSlotsPopup = () => {
    setShowSlotsPopup(false);
    setSelectedJob(null);
  };

  const getSelectedSlotsForBlock = (blockId) =>
    selectedSlotIdsByBlock[blockId] || [];

  const toggleSlotSelection = (blockId, slotId) => {
    setSelectedSlotIdsByBlock((prev) => {
      const cur = prev[blockId] || [];
      const has = cur.includes(slotId);
      const nextIds = has
        ? cur.filter((id) => id !== slotId)
        : [...cur, slotId];
      return { ...prev, [blockId]: nextIds };
    });
  };

  const clearSlotSelection = (blockId) => {
    setSelectedSlotIdsByBlock((prev) => {
      const next = { ...prev };
      delete next[blockId];
      return next;
    });
  };

  const openDeleteSelectedSlots = (blockId) => {
    const ids = getSelectedSlotsForBlock(blockId);
    if (ids.length === 0) return;
    setSlotsDeletePayload({ slotIds: [...ids] });
  };

  const confirmDeleteBlock = async () => {
    if (!blockToDelete) return;
    setDeletingBlockId(blockToDelete.id);
    try {
      await InterViewSlotService.deleteBlock(blockToDelete.id);
      showToast({ message: "Block deleted successfully.", type: "success" });
      setBlockToDelete(null);
      await fetchDetails();
    } catch (error) {
      showToast({
        message: error.message || "Failed to delete block.",
        type: "error",
      });
    } finally {
      setDeletingBlockId(null);
    }
  };

  const confirmDeleteSlots = async () => {
    if (!slotsDeletePayload?.slotIds?.length || !vacancyId || !statusId) return;
    const ids = slotsDeletePayload.slotIds;
    setDeletingSlots(true);
    try {
      if (ids.length === 1) {
        await InterViewSlotService.deleteSlot(ids[0]);
      } else {
        await InterViewSlotService.deleteSlotsBulk(vacancyId, statusId, ids);
      }
      showToast({
        message:
          ids.length === 1
            ? "Slot deleted successfully."
            : `${ids.length} slots deleted successfully.`,
        type: "success",
      });
      setSlotsDeletePayload(null);
      setSelectedSlotIdsByBlock((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((bid) => {
          next[bid] = next[bid].filter((id) => !ids.includes(id));
        });
        return next;
      });
      await fetchDetails();
    } catch (error) {
      showToast({
        message: error.message || "Failed to delete slot(s).",
        type: "error",
      });
    } finally {
      setDeletingSlots(false);
    }
  };

  const closeBlockDeleteDialog = () => {
    if (deletingBlockId) return;
    setBlockToDelete(null);
  };

  const closeSlotsDeleteDialog = () => {
    if (deletingSlots) return;
    setSlotsDeletePayload(null);
  };

  return (
    <>
      <div className="w-[95%] lg:w-[90%] mx-auto py-8 pb-10">
        {/* Back Button */}
        <PageHeader
          navigationText="Back to Interview Slots"
          navigateTo={handleBack}
          navButtonClass="flex items-center gap-2 px-4 py-2 bg-primary-dark hover:bg-primary-dark text-white font-medium rounded-lg transition-colors text-sm group w-fit"
        />

        {/* Page Title */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground mb-1">Slot Details</h1>
          {meta?.vacancy && (
            <p className="text-muted-foreground text-sm">
              {meta.vacancy.title}
              {meta.vacancy_status?.status?.name && (
                <span
                  className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold"
                  style={{
                    backgroundColor:
                      (meta.vacancy_status.status.bg_color || "#6366f1") + "20",
                    color: meta.vacancy_status.status.bg_color || "#6366f1",
                  }}
                >
                  {meta.vacancy_status.status.name}
                </span>
              )}
            </p>
          )}
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader
              className="w-8 h-8"
              bgColor="bg-transparent"
              loadingText="Loading details..."
            />
          </div>
        ) : blocks.length === 0 ? (
          <div className="bg-card rounded-xl shadow-sm border border-border px-6 py-12 text-center text-muted-foreground">
            No details available.
          </div>
        ) : (
          <div className="space-y-4">
            {blocks.map((block) => {
              const dateStr = block.date || block.start_time?.split(" ")[0];
              const edit = editState[block.id] || {};
              const timeline = buildTimeline(block);
              const isSaving = savingId === block.id;

              return (
                <div
                  key={block.id}
                  className="bg-card rounded-xl shadow-sm border border-border p-6"
                >
                  {/* Header row: Day title + Save button */}
                  <div className="flex flex-wrap sm:flex-row flex-col items-center justify-between gap-4 mb-5">
                    <h2 className="text-lg font-semibold text-foreground">
                      {formatFullDate(dateStr)}
                      <span className="ml-2 text-sm font-normal text-muted-foreground">
                        {formatDayName(dateStr)}
                      </span>
                    </h2>

                    {/* Editable fields */}
                    <div className="flex flex-wrap gap-2 items-end">
                      {/* From Time */}
                      <div className="border border-input rounded-lg px-3 py-2 min-w-[120px]">
                        <p className="text-xs text-muted-foreground mb-1">From Time</p>
                        <input
                          type="time"
                          value={edit.fromTime || ""}
                          onChange={(e) =>
                            handleFieldChange(
                              block.id,
                              "fromTime",
                              e.target.value,
                            )
                          }
                          className="text-sm font-medium text-foreground bg-background border-none outline-none w-full"
                        />
                      </div>

                      {/* To Time */}
                      <div className="border border-input rounded-lg px-3 py-2 min-w-[120px]">
                        <p className="text-xs text-muted-foreground mb-1">To Time</p>
                        <input
                          type="time"
                          value={edit.toTime || ""}
                          onChange={(e) =>
                            handleFieldChange(
                              block.id,
                              "toTime",
                              e.target.value,
                            )
                          }
                          className="text-sm font-medium text-foreground bg-background border-none outline-none w-full"
                        />
                      </div>

                      {/* Interview Type (from block; not editable here) */}
                      <div className="border border-input rounded-lg px-3 py-2 min-w-[150px]">
                        <p className="text-xs text-muted-foreground mb-1">
                          Interview Type
                        </p>
                        <p className="text-sm font-medium text-foreground min-h-[1.25rem]">
                          {block.interview_type || block.interviewType || "—"}
                        </p>
                      </div>

                      {/* Slot Duration */}
                      <div className="border border-input rounded-lg px-3 py-2 min-w-[140px]">
                        <p className="text-xs text-muted-foreground mb-1">
                          Slot Duration
                        </p>
                        <select
                          value={edit.slotDuration || ""}
                          onChange={(e) =>
                            handleFieldChange(
                              block.id,
                              "slotDuration",
                              e.target.value,
                            )
                          }
                          className="text-sm font-medium text-foreground bg-background border-none outline-none w-full cursor-pointer"
                        >
                          <option value="" className="bg-card text-foreground">
                            Select
                          </option>
                          {slotDurationOptions.map((d) => (
                            <option
                              key={d}
                              value={d}
                              className="bg-card text-foreground"
                            >
                              {d} Minutes
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Break Duration */}
                      <div className="border border-input rounded-lg px-3 py-2 min-w-[140px]">
                        <p className="text-xs text-muted-foreground mb-1">
                          Break Duration
                        </p>
                        <select
                          value={edit.breakDuration || "0"}
                          onChange={(e) =>
                            handleFieldChange(
                              block.id,
                              "breakDuration",
                              e.target.value,
                            )
                          }
                          className="text-sm font-medium text-foreground bg-background border-none outline-none w-full cursor-pointer"
                        >
                          {breakDurationOptions.map((d) => (
                            <option
                              key={d}
                              value={d}
                              className="bg-card text-foreground"
                            >
                              {d === 0 ? "No Break" : `${d} Minutes`}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Save Button */}
                    </div>
                  </div>

                  {/* Timeline */}
                  {timeline.length > 0 ? (
                    <>
                      <div className="flex flex-wrap gap-2 items-stretch">
                        {timeline.map((item, i) =>
                          item.type === "slot" ? (
                            <div
                              key={item.slot?.id ?? `slot-${i}`}
                              className={
                                (item.status == "open"
                                  ? "rounded-lg border border-border bg-card px-2 py-2 min-w-[140px]"
                                  : "rounded-lg border border-red-200 bg-red-50 px-2 py-2 min-w-[140px]") +
                                " hover:shadow-md transition-shadow transition-all"
                              }
                            >
                              <div className="flex items-start gap-2">
                                {slotIsDeletable(item) && (
                                  <label
                                    className="mt-0.5 flex cursor-pointer shrink-0"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={getSelectedSlotsForBlock(
                                        block.id,
                                      ).includes(item.slot.id)}
                                      onChange={() =>
                                        toggleSlotSelection(
                                          block.id,
                                          item.slot.id,
                                        )
                                      }
                                      className="h-3 w-3 rounded border-input text-primary focus:ring-primary"
                                    />
                                  </label>
                                )}
                                <div
                                  className={
                                    (item.status === "pending" ||
                                      item.status === "confirmed") &&
                                    item.slot
                                      ? "flex-1 min-w-0 cursor-pointer"
                                      : "flex-1 min-w-0"
                                  }
                                  onClick={() =>
                                    (item.status === "pending" ||
                                      item.status === "confirmed") &&
                                    item.slot &&
                                    handleViewSlots(block, item.slot)
                                  }
                                >
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-bold text-foreground capitalize leading-none">
                                      {item.status}
                                    </span>
                                    <span className="px-1.5 py-0.5 bg-muted-foreground text-white rounded text-[10px] font-bold leading-none">
                                      {item.duration} min
                                    </span>
                                  </div>
                                  <p className="text-xs font-medium text-muted-foreground">
                                    {item.from} - {item.to}
                                  </p>
                                </div>
                                {/* {slotIsDeletable(item) && (
                                                                    <button
                                                                        type="button"
                                                                        aria-label="Delete this slot"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSlotsDeletePayload({ slotIds: [item.slot.id] });
                                                                        }}
                                                                        className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-red-50 hover:text-red-600 transition-colors"
                                                                    >
                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                )} */}
                              </div>
                            </div>
                          ) : (
                            <div
                              key={`break-${i}`}
                              className="rounded-lg border-2 border-dashed border-input bg-muted px-1 py-2 flex items-center justify-center"
                            >
                              <p className="text-[12px] font-bold text-muted-foreground [writing-mode:vertical-lr] rotate-180 whitespace-nowrap">
                                {item.duration} min
                              </p>
                            </div>
                          ),
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">
                      {edit.fromTime && edit.toTime && edit.slotDuration
                        ? "No slots fit in the selected time range."
                        : "Fill in the fields above to preview slots."}
                    </p>
                  )}
                  <div className="flex justify-between items-center mt-4 flex-wrap gap-2">
                    <div className="flex-1 h-full">
                      {getSelectedSlotsForBlock(block.id).length > 0 && (
                        <div className="flex items-center justify-between rounded-lg border border-red-100 bg-red-50/80 px-4 py-1">
                          <p className="text-sm text-foreground">
                            <span className="font-semibold text-foreground">
                              {getSelectedSlotsForBlock(block.id).length}
                            </span>{" "}
                            open slot
                            {getSelectedSlotsForBlock(block.id).length === 1
                              ? ""
                              : "s"}{" "}
                            selected
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => clearSlotSelection(block.id)}
                              className="text-sm font-medium text-muted-foreground hover:text-foreground px-3 py-1.5 rounded-md hover:bg-muted transition-colors"
                            >
                              Clear
                            </button>
                            <button
                              type="button"
                              onClick={() => openDeleteSelectedSlots(block.id)}
                              className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1 text-sm font-semibold text-white shadow-sm hover:bg-red-700 transition-colors"
                            >
                              <Trash2 className="w-4 h-4 shrink-0" />
                              Delete selected
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-end gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => setBlockToDelete(block)}
                        disabled={!!savingId || !!deletingBlockId}
                        className="flex items-center gap-1.5 px-4 py-2.5 border border-red-200 text-red-700 bg-card rounded-lg hover:bg-red-50 transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSave(block)}
                        disabled={isSaving}
                        className="flex items-center gap-1.5 px-4 py-2.5 bg-primary-dark text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
                      >
                        {isSaving ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        {isSaving ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <DialogBoxSkeleton
        isOpen={showSlotsPopup}
        onClose={handleCloseSlotsPopup}
        disableBackdropClick={true}
      >
        <InterviewDetailsForm
          selectedJob={selectedJob}
          handleCloseSlots={handleCloseSlotsPopup}
          candidate={{ data: selectedJob?.booked_interview_slot?.slots?.[0] }}
          onSuccess={fetchDetails}
        />
      </DialogBoxSkeleton>

      <DialogBox
        isOpen={!!blockToDelete}
        onClose={closeBlockDeleteDialog}
        title="Delete block"
        className="w-[40%] max-md:w-[60%]"
        disableBackdropClick={!!deletingBlockId}
        actionButton={
          <button
            type="button"
            onClick={confirmDeleteBlock}
            disabled={!!deletingBlockId}
            className="inline-flex w-full justify-center items-center gap-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:pointer-events-none"
          >
            {deletingBlockId && (
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" />
            )}
            {deletingBlockId ? "Deleting…" : "Delete"}
          </button>
        }
      >
        <p className="text-muted-foreground text-sm mt-1 max-w-[390px]">
          Delete this entire day block and all of its slots? This cannot be
          undone.
        </p>
      </DialogBox>

      <DialogBox
        isOpen={!!slotsDeletePayload}
        onClose={closeSlotsDeleteDialog}
        title={
          slotsDeletePayload?.slotIds?.length === 1
            ? "Delete slot"
            : "Delete slots"
        }
        className="w-[40%] max-md:w-[60%]"
        disableBackdropClick={deletingSlots}
        actionButton={
          <button
            type="button"
            onClick={confirmDeleteSlots}
            disabled={deletingSlots}
            className="inline-flex w-full justify-center items-center gap-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:pointer-events-none"
          >
            {deletingSlots && (
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" />
            )}
            {deletingSlots ? "Deleting…" : "Delete"}
          </button>
        }
      >
        <p className="text-muted-foreground text-sm mt-1 max-w-[390px]">
          {slotsDeletePayload?.slotIds?.length === 1
            ? "Are you sure you want to delete this open time slot? This cannot be undone."
            : `Are you sure you want to delete ${slotsDeletePayload?.slotIds?.length ?? 0} open time slots? This cannot be undone.`}
        </p>
      </DialogBox>
    </>
  );
};

export default InterviewSlotDetails;
